import { FacebookMinimal, WhatsAppMinimal } from '@zappyrent/hundop';
import { debounce } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  StyledCtaButton,
  StyledCtaButtonWrapper,
  StyledCtaButtonsWrapper,
  StyledFormError,
  StyledGenerateLinkWrapper,
  StyledInputFieldWrapper,
  StyledLinkBox,
  StyledLinkBoxWrapper,
  StyledParagraphsWrapper,
  StyledSocialBoxLink,
  StyledSocialBoxList,
  StyledSocialBoxListItem,
  StyledSocialBoxWrapper,
  StyledWrapper,
} from './InviteALandlord.styles';
import { IInviteALandlordProps } from './InviteALandlord.types';
import referrerApi from '../../api/referrer/referrer';
import { copyToClipboard } from '../../helpers';
import InputField from '../UI/InputField';
import { StyledParagraph, StyledH3, StyledH2 } from '../UI/Typography';

const InviteALandlord = ({
  invitationLink,
  phoneNumber,
  onPhoneNumberSaved,
}: IInviteALandlordProps): JSX.Element => {
  const [isPhoneNumberSaved, setIsPhoneNumberSaved] = useState(false);
  const [isInvitationLinkCopied, setIsInvitationLinkCopied] = useState(false);
  const [isFormErrorVisible, setIsFormErrorVisible] = useState(false);
  const [localPhoneNumber, setLocalPhoneNumber] = useState({
    value: phoneNumber ?? '',
    initValue: phoneNumber ?? '',
  });
  const { t } = useTranslation(['common', 'form', 'product']);

  const updateReferrerFromApi = async () => {
    try {
      await referrerApi.updateReferrer({
        phone_no: localPhoneNumber.value,
      });
      setIsPhoneNumberSaved(true);
      onPhoneNumberSaved();
    } catch (e) {
      setIsPhoneNumberSaved(false);
      console.error(e);
    }
  };

  const handleLinkBoxClick = () => {
    if (invitationLink) {
      copyToClipboard(invitationLink);
      setIsInvitationLinkCopied(true);
    }
  };

  const handlePhoneNumberSubmit = () => {
    if (localPhoneNumber.value.length) {
      updateReferrerFromApi();
      setIsFormErrorVisible(false);
    } else {
      setIsFormErrorVisible(true);
    }
  };

  useEffect(() => {
    if (isInvitationLinkCopied) {
      const timeoutId = setTimeout(() => {
        setIsInvitationLinkCopied(false);
      }, 3000);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isInvitationLinkCopied]);

  useEffect(() => {
    setIsPhoneNumberSaved(!!phoneNumber);
  }, [phoneNumber]);

  return (
    <StyledWrapper>
      <StyledH2>{t('common:inviteALandlord')}</StyledH2>
      <StyledParagraphsWrapper>
        <StyledParagraph>
          {t('common:sendYourLinkToTheLandlord')}
        </StyledParagraph>
        <StyledParagraph>
          {t('common:weRentInTheFollowingCities')}
        </StyledParagraph>
      </StyledParagraphsWrapper>
      {invitationLink && (
        <>
          {isPhoneNumberSaved ? (
            <>
              <StyledH3>{t('common:inviteByLink')}</StyledH3>
              <StyledLinkBoxWrapper>
                <StyledLinkBox>{invitationLink}</StyledLinkBox>
                <StyledCtaButtonsWrapper>
                  <StyledCtaButton
                    href={invitationLink}
                    rel="noreferrer"
                    tag="a"
                    target="_blank"
                    title={t('common:openLink')}
                    user="landlord"
                    variant="primary"
                  >
                    {t('common:openLink')}
                  </StyledCtaButton>
                  <StyledCtaButton
                    tag="button"
                    title={t('common:copyLink')}
                    variant="primary"
                    onClick={handleLinkBoxClick}
                  >
                    {!isInvitationLinkCopied
                      ? t('common:copyLink')
                      : t('common:linkHasBeenCopied')}
                  </StyledCtaButton>
                </StyledCtaButtonsWrapper>
              </StyledLinkBoxWrapper>
              <StyledSocialBoxWrapper>
                <StyledH3>{t('common:inviteBySocialNetwork')}</StyledH3>
                <StyledSocialBoxList>
                  <StyledSocialBoxListItem>
                    <StyledSocialBoxLink
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                        invitationLink,
                      )}`}
                      rel="noreferrer"
                      target="_blank"
                      title={`${t('common:shareLinkOn')} Facebook`}
                    >
                      <span>Facebook</span>
                      <FacebookMinimal aria-hidden="true" />
                    </StyledSocialBoxLink>
                  </StyledSocialBoxListItem>
                  <StyledSocialBoxListItem>
                    <StyledSocialBoxLink
                      href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                        `${t('common:shareLinkText')} ${invitationLink}`,
                      )}`}
                      rel="noreferrer"
                      target="_blank"
                      title={`${t('common:shareLinkOn')} WhatsApp`}
                    >
                      <span>WhatsApp</span>
                      <WhatsAppMinimal aria-hidden="true" />
                    </StyledSocialBoxLink>
                  </StyledSocialBoxListItem>
                </StyledSocialBoxList>
              </StyledSocialBoxWrapper>
            </>
          ) : (
            <>
              <StyledParagraph>{t('common:createLinkHint')}</StyledParagraph>
              <StyledGenerateLinkWrapper>
                <StyledInputFieldWrapper>
                  <InputField
                    label={t('common:phoneNumber')}
                    value={localPhoneNumber.value}
                    setValue={setLocalPhoneNumber}
                    placeholder="+39 xxx xxxxxx"
                    type="tel"
                  />
                  {isFormErrorVisible && (
                    <StyledFormError>
                      {t('form:thisFieldIsMandatory')}
                    </StyledFormError>
                  )}
                </StyledInputFieldWrapper>
                <StyledCtaButtonWrapper>
                  <StyledCtaButton
                    variant="primary"
                    user="landlord"
                    tag="button"
                    title={t('common:createLink')}
                    onClick={debounce(handlePhoneNumberSubmit, 600, {
                      leading: true,
                    })}
                  >
                    {t('common:createLink')}
                  </StyledCtaButton>
                </StyledCtaButtonWrapper>
              </StyledGenerateLinkWrapper>
            </>
          )}
        </>
      )}
    </StyledWrapper>
  );
};

export default InviteALandlord;
